import { computed, ref, watch, type Ref } from 'vue';
import { trainingMatrixApi, type TrainingMatrixDocumentEntry } from '@/entities/Training';
import { documentApi } from '@/entities/Document';
import { useDocuments } from '@/features/Document';
import { useCustomToast } from '@/shared/lib';

export function useRoleChangeDocuments(
  positionId: Ref<number | null>,
  revisionId: Ref<number | null>,
  initialSelectedDocumentRevisionIds?: number[]
) {
  const toast = useCustomToast();
  const { viewDocument } = useDocuments();

  const documents = ref<TrainingMatrixDocumentEntry[]>([]);
  const selectedIds = ref<Set<number>>(new Set());
  const loading = ref(false);
  const error = ref(false);
  let latestRequestId = 0;
  let isFirstFetch = true;
  let pendingFetch: Promise<void> = Promise.resolve();

  async function fetchDocuments(): Promise<void> {
    const requestId = ++latestRequestId;

    if (positionId.value === null || revisionId.value === null) {
      documents.value = [];
      selectedIds.value = new Set();
      loading.value = false;
      error.value = false;
      return;
    }

    // Consumed only once real ids are available: the immediate watch below
    // fires once at construction time, before editContext has populated
    // positionId/revisionId, and that call must not burn the "first fetch"
    // flag — otherwise the actual first real fetch (once load() sets the
    // real ids) wrongly falls back to "select everything".
    const applyInitialSelection = isFirstFetch;
    isFirstFetch = false;

    loading.value = true;
    error.value = false;
    try {
      const result = await trainingMatrixApi.getRevisionDetail(revisionId.value, {
        positionId: positionId.value,
        allItems: true,
      });
      if (requestId !== latestRequestId) return;
      documents.value = result.data.positions.find((position) => position.id === positionId.value)?.documents ?? [];
      selectedIds.value =
        applyInitialSelection && initialSelectedDocumentRevisionIds
          ? new Set(initialSelectedDocumentRevisionIds)
          : new Set(documents.value.map((document) => document.id));
    } catch {
      if (requestId !== latestRequestId) return;
      documents.value = [];
      selectedIds.value = new Set();
      error.value = true;
      toast.showErrorToast({
        title: 'Erro ao carregar documentos',
        message: 'Não foi possível carregar os documentos da matriz de treinamentos.',
        type: 'error',
      });
    } finally {
      if (requestId === latestRequestId) loading.value = false;
    }
  }

  const selectedCount = computed(() => selectedIds.value.size);
  const allSelected = computed(() => documents.value.length > 0 && selectedIds.value.size === documents.value.length);
  const partiallySelected = computed(() => selectedIds.value.size > 0 && !allSelected.value);

  function isSelected(documentId: number): boolean {
    return selectedIds.value.has(documentId);
  }

  function toggle(documentId: number): void {
    const next = new Set(selectedIds.value);
    if (next.has(documentId)) {
      next.delete(documentId);
    } else {
      next.add(documentId);
    }
    selectedIds.value = next;
  }

  function toggleAll(): void {
    selectedIds.value = allSelected.value ? new Set() : new Set(documents.value.map((document) => document.id));
  }

  function selectedDocumentRevisionIds(): number[] {
    return [...selectedIds.value];
  }

  async function viewDocumentRevision(documentRevisionId: number): Promise<void> {
    try {
      const revision = await documentApi.getRevisionById(documentRevisionId);
      await viewDocument(revision.document.id.toString(), undefined, documentRevisionId);
    } catch {
      toast.showErrorToast({
        title: 'Erro ao abrir documento',
        message: 'Não foi possível abrir este documento.',
        type: 'error',
      });
    }
  }

  // flush: 'sync' so a caller's applyInitial() setting real position/revision ids
  // synchronously (edit mode) kicks off the real fetch in the same tick, letting
  // whenReady() below observe it immediately instead of a stale, already-resolved promise.
  watch(
    [positionId, revisionId],
    () => {
      pendingFetch = fetchDocuments();
    },
    { immediate: true, flush: 'sync' }
  );

  /** Lets a form's load() await the fetch triggered by the position/revision ids it just set. */
  async function whenReady(): Promise<void> {
    await pendingFetch;
  }

  return {
    documents,
    loading,
    error,
    selectedCount,
    allSelected,
    partiallySelected,
    isSelected,
    toggle,
    toggleAll,
    selectedDocumentRevisionIds,
    viewDocumentRevision,
    whenReady,
  };
}
